import differenceInDays from 'date-fns/differenceInDays'
import parseISO from 'date-fns/parseISO'
import Settings from 'Settings'
import {MeasurementSystemType} from 'types/Localization'
import {fetchUserIPData} from 'utils/fetchUserIPData'
import {getLanguageFromBrowser} from 'utils/language'

import {getBrandCodeFromPartnerKey} from '@daedalus/core/src/brand/utils'
import {
  availableCountries,
  defaultCountry,
  getCountryAttribute
} from '@daedalus/core/src/localization/business/countries'
import {enabledLanguages} from '@daedalus/core/src/localization/services/locale'
import {DEFAULT_ROOMS} from '@daedalus/core/src/room/business/roomConfiguration/config'
import {validateSearchParamDates} from '@daedalus/core/src/searchParams/validateSearchParamDates'
import {getOrGenerateAnonymousId} from '@daedalus/core/src/utils/anonymousId'
import {
  addDaysToDate,
  calculateDateDifference
} from '@daedalus/core/src/utils/date'
import {removeKeyCaseSensitivity} from '@daedalus/core/src/utils/object'

import {Language} from '../../types/partnersApi/types'

const testPartnerKey = Settings.get('REACT_APP_TEST_PARTNER_KEY')

const allowedUserDevice = ['mobile', 'tablet', 'desktop'] as const

const MAX_DATE_DIFFERENCE = 7

export interface WidgetConfig {
  partnerKey: string
  hotelNames: string[]
  hotelIds: string[]
  distanceUnit: MeasurementSystemType
  country?: string
  city?: string
  checkIn?: string
  checkOut?: string
  currency: string
  language: NonNullable<Language>
  rooms: string
  anonymousId: string
  userCountry: string
  userDevice?: (typeof allowedUserDevice)[number]
  affiliateId?: string
  placeName?: string
  placeId?: string
  iataCode?: string
  forcedUserCountry?: string
  label?: string
  brandCode: string
  // Quick fix for the Kiwi widget integration
  // TODO: Remove after implementing case-insensitive param parsing.
  checkin?: string
  checkout?: string
}

interface UrlParams
  extends Partial<Omit<WidgetConfig, 'hotelNames' | 'hotelIds'>> {
  hotelNames?: string
  hotelIds?: string
}

const MULTIPLE_URL_PARAMS_SEPARATOR = ','

/**
 * Updates checkOut date if it is too far from checkIn date
 * @param checkIn - check in date
 * @param checkOut - check out date
 * @returns updated check out date
 */
const getUpdatedCheckOut = (
  checkIn: string | undefined,
  checkOut: string | undefined
) => {
  if (!checkIn || !checkOut) return checkOut

  return calculateDateDifference(new Date(checkIn), new Date(checkOut)) >
    MAX_DATE_DIFFERENCE
    ? addDaysToDate(checkIn, MAX_DATE_DIFFERENCE)
    : checkOut
}

export const buildWidgetConfig = async (
  urlParams: UrlParams
): Promise<WidgetConfig> => {
  const {
    partnerKey,
    hotelNames,
    hotelIds,
    country,
    city,
    checkIn,
    checkOut,
    currency,
    language,
    rooms,
    userDevice,
    userCountry,
    placeName,
    placeId,
    iataCode,
    label
  } = removeKeyCaseSensitivity<UrlParams>(urlParams)

  const isProductionOrTestEnv =
    Settings.get('REACT_APP_ENV') === 'production' ||
    Settings.get('REACT_APP_ENV') === 'test'

  // For local development partnerKey is not required, it will default to the test key
  if (!partnerKey && isProductionOrTestEnv) {
    throw new Error('Missing Partner key')
  }

  const configPartnerKey = partnerKey || testPartnerKey

  const updatedCheckOut = getUpdatedCheckOut(checkIn, checkOut)

  const {checkIn: validatedCheckIn, checkOut: validatedCheckOut} =
    validateSearchParamDates({
      checkIn,
      checkOut: updatedCheckOut
    })

  const isValidForcedDates =
    validatedCheckIn &&
    validatedCheckOut &&
    checkIn === validatedCheckIn &&
    updatedCheckOut === validatedCheckOut &&
    differenceInDays(parseISO(validatedCheckOut), parseISO(validatedCheckIn)) >=
      1

  const isProvidedCountrySupported =
    !!userCountry && !!availableCountries[userCountry]

  const updatedUserCountry = isProvidedCountrySupported
    ? userCountry
    : defaultCountry.code

  const forcedUserCountry = isProvidedCountrySupported ? userCountry : undefined

  const anonymousId = urlParams?.anonymousId || getOrGenerateAnonymousId()

  const config: WidgetConfig = {
    partnerKey: configPartnerKey,
    hotelNames: hotelNames
      ? hotelNames.split(MULTIPLE_URL_PARAMS_SEPARATOR).map(item => item.trim())
      : [],
    hotelIds: hotelIds
      ? hotelIds.split(MULTIPLE_URL_PARAMS_SEPARATOR).map(item => item.trim())
      : [],
    country,
    city,
    checkIn: isValidForcedDates ? validatedCheckIn : undefined,
    checkOut: isValidForcedDates ? validatedCheckOut : undefined,
    currency:
      currency || getCountryAttribute(updatedUserCountry, 'currencyCode'),
    language: getLanguage(),
    rooms: rooms || DEFAULT_ROOMS,
    anonymousId,
    userCountry: updatedUserCountry,
    userDevice:
      userDevice && allowedUserDevice.includes(userDevice)
        ? userDevice
        : undefined,
    distanceUnit: getCountryAttribute(updatedUserCountry, 'measurementSystem'),
    placeName,
    placeId,
    iataCode,
    forcedUserCountry,
    label,
    brandCode: getBrandCodeFromPartnerKey(configPartnerKey)
  }

  if (!isProvidedCountrySupported) {
    return fetchUserIPData()
      .then(({country}) => {
        if (country && availableCountries[country]) {
          const detectedCountry = availableCountries[country]

          config.userCountry = detectedCountry.code
          config.currency = currency || detectedCountry.currencyCode
          config.distanceUnit = detectedCountry.measurementSystem
        }

        return config
      })
      .catch(() => {
        return config
      })
  }

  return config

  function getLanguage() {
    const updatedLanguage =
      language && enabledLanguages.includes(language)
        ? language
        : getLanguageFromBrowser()

    if (updatedLanguage.startsWith('en-')) {
      // Partners API does not support en-GB or en-US, only en
      return 'en'
    }

    return updatedLanguage
  }
}
